import { z } from 'zod';

/**
 * Team identities: a colour, a shape, and a name (D-20).
 *
 * Three constraints shape this list, and they are tighter than a normal web
 * app's:
 *
 * 1. **Colour is never the only signal.** Every entry carries a shape as well,
 *    so a team is identifiable to a colour-blind student and in a photograph
 *    of a washed-out projector.
 * 2. **Distinguishable under colour vision deficiency.** The hues are the
 *    Okabe-Ito qualitative palette, which is designed to stay separable under
 *    protanopia, deuteranopia and tritanopia.
 * 3. **Contrast is declared, not assumed.** Each entry names the text colour to
 *    use on it. Black on orange looks unusual next to white on blue, but the
 *    alternative is a label nobody at the back of the room can read. A unit
 *    test asserts every pair clears WCAG AA at 4.5:1.
 */

export const teamShapeSchema = z.enum([
  'circle',
  'triangle',
  'square',
  'diamond',
  'hexagon',
  'star',
]);

export type TeamShape = z.infer<typeof teamShapeSchema>;

export interface TeamIdentity {
  /** Stable key persisted in `Team.color`, so a rename never loses the shape. */
  key: string;
  /** Icelandic, agreeing with the neuter noun "lið". */
  name: string;
  color: string;
  /** Text colour that clears AA against `color`. */
  onColor: string;
  shape: TeamShape;
}

export const TEAM_IDENTITIES: readonly TeamIdentity[] = [
  { key: 'blue', name: 'Blátt lið', color: '#0072B2', onColor: '#FFFFFF', shape: 'circle' },
  { key: 'vermillion', name: 'Rautt lið', color: '#D55E00', onColor: '#000000', shape: 'triangle' },
  { key: 'green', name: 'Grænt lið', color: '#009E73', onColor: '#000000', shape: 'square' },
  { key: 'purple', name: 'Fjólublátt lið', color: '#CC79A7', onColor: '#000000', shape: 'diamond' },
  {
    key: 'orange',
    name: 'Appelsínugult lið',
    color: '#E69F00',
    onColor: '#000000',
    shape: 'hexagon',
  },
  { key: 'sky', name: 'Ljósblátt lið', color: '#56B4E9', onColor: '#000000', shape: 'star' },
] as const;

/** How many teams a freshly opened lobby starts with (Q-W). */
export const DEFAULT_TEAM_COUNT = 4;

export const MAX_TEAMS = TEAM_IDENTITIES.length;

/**
 * The identity for a slot, by index.
 *
 * Wraps rather than running out: a teacher who deletes and re-adds teams
 * repeatedly should get a usable colour, not an error. Beyond `MAX_TEAMS` the
 * shapes repeat, which is why team creation is capped at `MAX_TEAMS`.
 */
export function identityForSlot(index: number): TeamIdentity {
  const identity = TEAM_IDENTITIES[Math.abs(index) % TEAM_IDENTITIES.length];
  // Provably in range; the assertion is for `noUncheckedIndexedAccess`.
  if (identity === undefined) throw new Error('team palette is empty');
  return identity;
}

/** Looks an identity up by the key stored in `Team.color`. */
export function identityForKey(key: string): TeamIdentity {
  return TEAM_IDENTITIES.find((i) => i.key === key) ?? identityForSlot(0);
}
